import { useState, useEffect, useRef } from 'react';

const MARKETS = [
  {
    id: 1, cat: 'economics', catLabel: 'Economics', catColor: '#60a5fa', catBg: 'rgba(96,165,250,0.15)',
    topPick: true, resolution: 'Resolves 17 Sep 2026',
    question: 'Will the Federal Reserve cut interest rates at the September 2026 FOMC meeting?',
    marketPct: 74, aiPct: 82, edge: 8, edgeDir: 'pos', edgeAction: 'Back YES — market underpricing',
    signals: [
      'CPI trending to 2.3%; within striking distance of 2% target',
      'Unemployment ticked up to 4.4% — dual mandate tilting to jobs',
      'Fed dot plot: 2 cuts signalled for H2 2026',
    ],
    confidence: 'HIGH', volume: 'Polymarket vol: $18.2M',
  },
  {
    id: 2, cat: 'economics', catLabel: 'Economics', catColor: '#60a5fa', catBg: 'rgba(96,165,250,0.15)',
    topPick: true, resolution: 'Resolves Oct 2026',
    question: 'Will the ECB cut rates again before December 2026?',
    marketPct: 65, aiPct: 73, edge: 8, edgeDir: 'pos', edgeAction: 'Back YES — Eurozone stagnation underweighted',
    signals: [
      'Eurozone Q2 growth: 0.3% — stagnation pressure building',
      'ECB forward guidance shifted dovish at July meeting',
      'EUR strength vs USD limiting export recovery and inflation',
    ],
    confidence: 'HIGH', volume: 'Kalshi vol: $9.4M',
  },
  {
    id: 3, cat: 'weather', catLabel: 'Weather', catColor: '#34d399', catBg: 'rgba(52,211,153,0.15)',
    topPick: true, resolution: 'Resolves 30 Nov 2026',
    question: 'Will a Category 3+ Atlantic hurricane make US mainland landfall in 2026?',
    marketPct: 64, aiPct: 72, edge: 8, edgeDir: 'pos', edgeAction: 'Back YES — active season signals underpriced',
    signals: [
      'CSU forecast: 145% of average ACE index — hyperactive season',
      'La Niña conditions developing; historically increases Gulf activity',
      'Atlantic sea surface temps 1.2°C above 30-year mean in MDR',
    ],
    confidence: 'HIGH', volume: 'Polymarket vol: $6.1M',
  },
  {
    id: 4, cat: 'weather', catLabel: 'Weather', catColor: '#34d399', catBg: 'rgba(52,211,153,0.15)',
    topPick: true, resolution: 'Resolves Dec 2026',
    question: "Will 2026 be confirmed as Earth's hottest year on record?",
    marketPct: 51, aiPct: 59, edge: 8, edgeDir: 'pos', edgeAction: 'Back YES — Jan–Jul anomaly stronger than model consensus',
    signals: [
      'Jan–Jul running +0.42°C above 2023 record pace (ERA5 data)',
      'El Niño remnant forcing fading slower than ENSO models projected',
      'Arctic sea ice at record August low — positive feedback loop active',
    ],
    confidence: 'MEDIUM', volume: 'Metaculus: 4,200 forecasters',
  },
  {
    id: 5, cat: 'politics', catLabel: 'Politics', catColor: '#c4b5fd', catBg: 'rgba(167,139,250,0.15)',
    topPick: true, resolution: 'Resolves Dec 2026',
    question: 'Will a US government shutdown occur in fiscal year 2027 (Oct–Dec 2026)?',
    marketPct: 38, aiPct: 46, edge: 8, edgeDir: 'pos', edgeAction: 'Back YES — deadline risk underweighted',
    signals: [
      'CR extended twice already; Oct 1 deadline approaching with no deal',
      'Hardline House caucus blocking compromise on spending offsets',
      'Historical base rate: 35% of deadlines result in shutdown since 2010',
    ],
    confidence: 'MEDIUM', volume: 'Kalshi vol: $3.8M',
  },
  {
    id: 6, cat: 'global', catLabel: 'Global', catColor: '#f87171', catBg: 'rgba(248,113,113,0.12)',
    topPick: false, resolution: 'Resolves Dec 2026',
    question: "Will India overtake Japan as the world's 3rd-largest economy by nominal GDP in 2026?",
    marketPct: 58, aiPct: 65, edge: 7, edgeDir: 'pos', edgeAction: 'Back YES — yen weakness and India services underweighted',
    signals: [
      'IMF projects India $4.34T vs Japan $4.11T for 2026 fiscal year',
      'JPY remains weak at ¥148/$; structural gap widening faster than expected',
      'India IT and services sector grew 8.4% YoY — outpacing all G20 peers',
    ],
    confidence: 'MEDIUM', volume: 'Polymarket vol: $2.1M',
  },
  {
    id: 7, cat: 'politics', catLabel: 'Politics', catColor: '#c4b5fd', catBg: 'rgba(167,139,250,0.15)',
    topPick: false, resolution: 'Resolves Nov 2026',
    question: 'Will Republicans retain control of the US House after the November 2026 midterms?',
    marketPct: 61, aiPct: 55, edge: -6, edgeDir: 'neg', edgeAction: 'Back NO — market overprices incumbent advantage',
    signals: [
      'Historical midterm swing against president\'s party: −24 seats avg',
      'Generic ballot gap narrowed to R+2 from R+5 in May; trend toward D',
      'Suburban college-educated women shifting further D since 2022',
    ],
    confidence: 'MEDIUM', volume: 'Polymarket vol: $24.7M',
  },
  {
    id: 8, cat: 'economics', catLabel: 'Economics', catColor: '#60a5fa', catBg: 'rgba(96,165,250,0.15)',
    topPick: false, resolution: 'Resolves Dec 2026',
    question: 'Will US headline CPI remain below 3.0% for every month through year-end 2026?',
    marketPct: 62, aiPct: 53, edge: -9, edgeDir: 'neg', edgeAction: 'Back NO — market overconfident in disinflation',
    signals: [
      'Services CPI sticky at 4.1%; owner-equivalent rent rebounding',
      'Used vehicle prices rising again after 18-month decline',
      'Tariff pass-through effect typically lags 4–6 months; not yet in data',
    ],
    confidence: 'MEDIUM', volume: 'Kalshi vol: $5.6M',
  },
  {
    id: 9, cat: 'tech', catLabel: 'Tech', catColor: '#fbbf24', catBg: 'rgba(251,191,36,0.12)',
    topPick: false, resolution: 'Resolves Dec 2026',
    question: 'Will Apple announce a major AI company acquisition or partnership worth $1B+ in 2026?',
    marketPct: 44, aiPct: 36, edge: -8, edgeDir: 'neg', edgeAction: 'Back NO — Apple\'s M&A history argues against this',
    signals: [
      'Apple has made zero acquisitions above $400M since Beats (2014)',
      'WWDC 2026 focused entirely on on-device inference; no partnership signals',
      'EU regulatory scrutiny of big tech M&A creates deterrent',
    ],
    confidence: 'HIGH', volume: 'Polymarket vol: $3.2M',
  },
  {
    id: 10, cat: 'politics', catLabel: 'Politics', catColor: '#c4b5fd', catBg: 'rgba(167,139,250,0.15)',
    topPick: false, resolution: 'Resolves Dec 2027',
    question: 'Will the UK hold a general election before January 2028?',
    marketPct: 19, aiPct: 13, edge: -6, edgeDir: 'neg', edgeAction: 'Back NO — market overprices instability',
    signals: [
      'Labour majority of 173 seats — no incentive to call snap election',
      'Fixed-term Parliament tradition; next election due Jan 2029',
      "PM's approval at −18% but Labour still ahead of opposition in polls",
    ],
    confidence: 'HIGH', volume: 'Betfair vol: £1.4M',
  },
];

const FILTERS = [
  { id: 'all', label: 'All markets' },
  { id: 'politics', label: '🗳 Politics' },
  { id: 'economics', label: '📊 Economics' },
  { id: 'weather', label: '🌩 Weather' },
  { id: 'tech', label: '💻 Tech' },
  { id: 'global', label: '🌍 Global' },
];

const CONF_STYLE = {
  HIGH:   { background: 'rgba(52,211,153,0.12)', color: '#34d399' },
  MEDIUM: { background: 'rgba(251,191,36,0.12)',  color: '#fbbf24' },
  LOW:    { background: 'rgba(148,163,184,0.12)', color: 'rgba(232,237,245,0.42)' },
};

function ProbBar({ pct, color, animated }) {
  return (
    <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(148,163,184,0.12)', overflow: 'hidden' }}>
      <div style={{
        height: '100%', borderRadius: 3,
        background: color,
        width: animated ? `${pct}%` : '0%',
        transition: 'width 1s cubic-bezier(0.16,1,0.3,1)',
        opacity: color === '#60a5fa' ? 0.7 : 1,
      }} />
    </div>
  );
}

function MarketCard({ m, animated }) {
  const isMobile = window.innerWidth < 640;
  const edgeAbs = Math.abs(m.edge);

  return (
    <div style={{
      background: '#1a2640',
      border: m.topPick ? '1px solid rgba(251,191,36,0.3)' : '1px solid rgba(148,163,184,0.10)',
      borderRadius: 12,
      padding: isMobile ? 16 : 20,
      display: 'flex', flexDirection: 'column', gap: 14,
      position: 'relative', overflow: 'hidden',
    }}>
      {m.topPick && (
        <span style={{
          position: 'absolute', top: 12, right: 14,
          fontSize: 10, fontWeight: 700, color: '#fbbf24', letterSpacing: '0.1em',
        }}>⭐ TOP PICK</span>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
          padding: '3px 8px', borderRadius: 4,
          background: m.catBg, color: m.catColor,
        }}>{m.catLabel}</span>
        <span style={{
          marginLeft: 'auto', fontSize: 11, color: 'rgba(232,237,245,0.5)',
          fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
        }}>{m.resolution}</span>
      </div>

      {/* Question */}
      <div style={{
        fontSize: 15, fontWeight: 700, lineHeight: 1.5, color: '#e8edf5',
        paddingRight: m.topPick ? 90 : 0,
      }}>{m.question}</div>

      {/* Probability bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', width: 52, color: '#60a5fa', flexShrink: 0 }}>Market</span>
          <ProbBar pct={m.marketPct} color="#60a5fa" animated={animated} />
          <span style={{ fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums', width: 38, textAlign: 'right', color: '#60a5fa', flexShrink: 0 }}>{m.marketPct}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', width: 52, color: '#34d399', flexShrink: 0 }}>AI</span>
          <ProbBar pct={m.aiPct} color="#34d399" animated={animated} />
          <span style={{ fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums', width: 38, textAlign: 'right', color: '#34d399', flexShrink: 0 }}>{m.aiPct}%</span>
        </div>
      </div>

      {/* Edge */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
          fontSize: 12, fontWeight: 800, padding: '5px 10px', borderRadius: 6,
          fontVariantNumeric: 'tabular-nums',
          background: m.edgeDir === 'pos' ? 'rgba(52,211,153,0.22)' : 'rgba(248,113,113,0.18)',
          color: m.edgeDir === 'pos' ? '#34d399' : '#f87171',
        }}>
          {m.edgeDir === 'pos' ? '▲' : '▼'} {m.edgeDir === 'pos' ? '+' : '−'}{edgeAbs}%
        </span>
        <span style={{ fontSize: 12.5, color: 'rgba(232,237,245,0.75)', fontWeight: 500, lineHeight: 1.5, paddingTop: 3 }}>{m.edgeAction}</span>
      </div>

      {/* Signals */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {m.signals.map((s, i) => (
          <div key={i} style={{ fontSize: 13, color: 'rgba(232,237,245,0.72)', lineHeight: 1.5, paddingLeft: 16, position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0, color: 'rgba(232,237,245,0.45)', fontSize: 12, top: 1 }}>→</span>
            {s}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid rgba(148,163,184,0.12)', marginTop: 'auto' }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', padding: '4px 10px', borderRadius: 4, ...CONF_STYLE[m.confidence] }}>
          {m.confidence === 'HIGH' ? 'High confidence' : m.confidence === 'MEDIUM' ? 'Medium confidence' : 'Low confidence'}
        </span>
        <span style={{ fontSize: 11.5, color: 'rgba(232,237,245,0.45)', fontVariantNumeric: 'tabular-nums' }}>{m.volume}</span>
      </div>
    </div>
  );
}

export default function PredictionMarketsPage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [animated, setAnimated] = useState(false);
  const isMobile = window.innerWidth < 640;
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (hasAnimated.current) return;
    hasAnimated.current = true;
    const t = setTimeout(() => setAnimated(true), 150);
    return () => clearTimeout(t);
  }, []);

  const visible = MARKETS.filter(m => activeFilter === 'all' || m.cat === activeFilter);
  const topPickCount = MARKETS.filter(m => m.topPick).length;
  const avgEdge = (MARKETS.filter(m => m.topPick).reduce((s, m) => s + Math.abs(m.edge), 0) / topPickCount).toFixed(1);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '16px 12px' : '24px 24px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid rgba(148,163,184,0.10)' }}>
        <div>
          <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 800, letterSpacing: '-0.02em', color: '#e8edf5' }}>
            Prediction Markets
          </div>
          <div style={{ fontSize: 12, color: 'rgba(232,237,245,0.42)', marginTop: 3 }}>
            AI probabilities vs. market consensus · Updated 14 Aug 2026
          </div>
        </div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.25)',
          borderRadius: 20, padding: '4px 12px',
          fontSize: 11, fontWeight: 600, color: '#34d399',
          letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', display: 'inline-block', animation: 'pulse 2s ease-in-out infinite' }} />
          AI Model Live
        </span>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, overflowX: 'auto', borderBottom: '1px solid rgba(148,163,184,0.10)', paddingBottom: 20 }}>
        {[
          { value: MARKETS.length, label: 'Markets tracked', color: '#e8edf5' },
          { value: topPickCount, label: `Top picks (edge ≥7%)`, color: '#34d399' },
          { value: `+${avgEdge}%`, label: 'Avg edge on top picks', color: '#fbbf24' },
          { value: 5, label: 'Categories', color: '#e8edf5' },
        ].map((s, i) => (
          <div key={i} style={{ paddingRight: 28, marginRight: 28, borderRight: i < 3 ? '1px solid rgba(148,163,184,0.10)' : 'none', flexShrink: 0 }}>
            <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'rgba(232,237,245,0.42)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setActiveFilter(f.id)} style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12.5, fontWeight: 600,
            border: activeFilter === f.id ? '1px solid rgba(52,211,153,0.35)' : '1px solid rgba(148,163,184,0.22)',
            color: activeFilter === f.id ? '#34d399' : 'rgba(232,237,245,0.42)',
            background: activeFilter === f.id ? 'rgba(52,211,153,0.12)' : 'transparent',
            cursor: 'pointer', letterSpacing: '0.02em', transition: 'all 0.15s',
          }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : window.innerWidth < 1024 ? 'repeat(2,1fr)' : 'repeat(3,1fr)',
        gap: 16,
      }}>
        {visible.map(m => (
          <MarketCard key={m.id} m={m} animated={animated} />
        ))}
      </div>

      {/* Disclaimer */}
      <div style={{ marginTop: 32, fontSize: 11.5, color: 'rgba(232,237,245,0.22)', lineHeight: 1.6 }}>
        AI probability estimates are generated by BetBudAI's model and are not financial or investment advice. Prediction markets involve risk — probabilities can change rapidly. Volume figures sourced from public market data. Edge = AI model probability minus last-observed market price. Always verify current market prices before acting.
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.3} }`}</style>
    </div>
  );
}
